import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

// Session timeout in milliseconds (30 minutes default, can be configured from backend)
const DEFAULT_SESSION_TIMEOUT = 30 * 60 * 1000;

// Helper function to decode user from JWT token - defined outside component
const decodeUserFromToken = () => {
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.exp * 1000 > Date.now()) {
        return {
          id: payload.sub,
          email: payload.email,
          role: payload.role,
          full_name: payload.full_name || payload.email
        };
      } else {
        // Token expired, remove it
        localStorage.removeItem('token');
      }
    } catch (e) {
      // Token is invalid, remove it
      localStorage.removeItem('token');
    }
  }
  return null;
};

export const AuthProvider = ({ children }) => {
  // Initialize user synchronously from token
  const [user, setUser] = useState(() => decodeUserFromToken());
  const [loading, setLoading] = useState(true);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(0); // 0 = never sign out for inactivity
  const [sessionExpired, setSessionExpired] = useState(false);
  
  // Refs for activity tracking
  const lastActivityRef = useRef(Date.now());
  const timeoutIdRef = useRef(null);
  const warningTimeoutRef = useRef(null);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    localStorage.setItem('lastActivity', lastActivityRef.current.toString());
  }, []);

  // Handle session timeout
  const handleSessionTimeout = useCallback(() => {
    console.log('Session timeout triggered');
    setSessionExpired(true);
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    setUser(null);
  }, []);

  // Reset session timer
  const resetSessionTimer = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    
    // 0 = no inactivity sign-out: people stay signed in until they press Logout.
    if (!sessionTimeoutMinutes || sessionTimeoutMinutes <= 0) return;
    const timeoutMs = sessionTimeoutMinutes * 60 * 1000;
    
    // Set timer for session timeout
    timeoutIdRef.current = setTimeout(() => {
      handleSessionTimeout();
    }, timeoutMs);
  }, [sessionTimeoutMinutes, handleSessionTimeout]);

  // Check for activity and reset timer
  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      updateActivity();
      resetSessionTimer();
      setSessionExpired(false);
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer setup
    resetSessionTimer();

    // Check stored last activity on mount
    const storedActivity = localStorage.getItem('lastActivity');
    if (storedActivity && sessionTimeoutMinutes > 0) {
      const elapsed = Date.now() - parseInt(storedActivity, 10);
      const timeoutMs = sessionTimeoutMinutes * 60 * 1000;
      if (elapsed > timeoutMs) {
        handleSessionTimeout();
      }
    }

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
    };
  }, [user, sessionTimeoutMinutes, updateActivity, resetSessionTimer, handleSessionTimeout]);

  // Fetch session config from backend
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await authAPI.getSessionConfig();
        if (typeof response.data.session_timeout_minutes === 'number') {
          setSessionTimeoutMinutes(response.data.session_timeout_minutes);
        }
      } catch (error) {
        // Use default timeout if config fetch fails
        console.log('Using default session timeout');
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await authAPI.getMe();
        setUser(response.data);
        updateActivity();
        // Renew the login on every visit so closing the tab never signs anyone out.
        authAPI.refresh()
          .then((r) => { if (r.data?.access_token) localStorage.setItem('token', r.data.access_token); })
          .catch(() => {});
        // Sync the user's browser-detected timezone to the backend so emails render correctly
        try {
          const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (browserTz && browserTz !== response.data?.timezone) {
            authAPI.updateTimezone(browserTz).catch(() => {});
          }
        } catch {}
      } catch (error) {
        // Only remove token if it's actually invalid (401)
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          setUser(null);
        } else {
          // For network errors, keep the user from token payload
          const tokenUser = decodeUserFromToken();
          if (tokenUser) {
            setUser(tokenUser);
          }
        }
      }
    }
    setLoading(false);
  };

  const login = async (credentials) => {
    const response = await authAPI.login(credentials);
    localStorage.setItem('token', response.data.access_token);
    setUser(response.data.user);
    updateActivity();
    setSessionExpired(false);
    // Sync browser timezone on login
    try {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTz && browserTz !== response.data.user?.timezone) {
        authAPI.updateTimezone(browserTz).catch(() => {});
      }
    } catch {}
    return response.data;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    localStorage.setItem('token', response.data.access_token);
    setUser(response.data.user);
    updateActivity();
    setSessionExpired(false);
    // Sync browser timezone on register
    try {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTz) {
        authAPI.updateTimezone(browserTz).catch(() => {});
      }
    } catch {}
    return response.data;
  };

  const logout = useCallback((redirectTo) => {
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('bge_acting_as');
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    // Leaving via a full page load means the dashboard never re-renders with
    // user=null, which made its protected-route redirect fight our navigation
    // and loop until the error screen showed (most visibly on phones).
    if (typeof redirectTo === 'string') {
      sessionStorage.setItem('bge_logged_out', '1');
      window.location.replace(redirectTo);
      return;
    }
    setUser(null);
    setSessionExpired(false);
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
  }, []);

  /**
   * loginWithToken — used by OAuth flows (e.g., Google Sign-In callback) that
   * already have a signed JWT + user object from the backend. Skips the
   * /api/auth/login call and behaves identically otherwise.
   */
  const loginWithToken = useCallback((token, userObj) => {
    localStorage.setItem('token', token);
    setUser(userObj);
    updateActivity();
    setSessionExpired(false);
    try {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTz && browserTz !== userObj?.timezone) {
        authAPI.updateTimezone(browserTz).catch(() => {});
      }
    } catch { /* no-op */ }
  }, [updateActivity]);

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout, 
      loginWithToken,
      sessionExpired, 
      clearSessionExpired,
      sessionTimeoutMinutes,
      updateUser: (patch) => setUser((u) => (u ? { ...u, ...patch } : u)),
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
